/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, use, useCallback, useEffect, useMemo, useRef, useTransition } from 'react';
import {
    type ShouldRevalidateFunctionArgs,
    useAsyncError,
    useLocation,
    useNavigation,
    useRouteLoaderData,
} from 'react-router';
import type { loader as rootLoader } from '@/root';
import type { Route } from './+types/_app.$categoryId';
import type { ShopperProducts, ShopperSearch } from '@/scapi';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { fetchCategory } from '@/lib/api/categories.server';
import { fetchSearchProducts } from '@/lib/api/search.server';
import { fetchWishlistInitialState } from '@/lib/wishlist/fetch-initial-state.server';
import type { WishlistInitialState } from '@/lib/wishlist/state';
import { WishlistProvider } from '@/providers/wishlist';
import { getAllQueryParams, getQueryParam, PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import CategoryBreadcrumbs from '@/components/category-breadcrumbs';
import { BreadcrumbSection } from '@/components/breadcrumb-section';
import CategoryPagination from '@/components/category-pagination';
import CategorySorting from '@/components/category-sorting';
import DeferredProductGrid from '@/components/product-grid';
import { useAnalytics } from '@/hooks/use-analytics';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { type WidenImageValue } from '@/components/widen-image';
import { type DefaultSettingsValue, type DefaultSettingsTheme } from '@/lib/page-designer/default-settings';
import { resolveLastComponentTheme } from '@/lib/page-designer/last-component-theme';
import { ArrowDownRightIcon } from '@/components/icons';
import { cn } from '@/lib/utils';
import { Region } from '@/components/region';
import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import CategoryBanner from '@/components/category-banner';
import CategoryBannerSkeleton from '@/components/category-banner/skeleton';
import { JsonLd } from '@/components/json-ld';
import { SeoMeta } from '@/components/seo-meta';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
import { generateCategorySchema } from '@/utils/category-schema';
import { buildCategoryCrumbs, generateBreadcrumbSchema, type BreadcrumbListSchema } from '@/utils/breadcrumb-schema';
import { generateArticleSchema, type ArticleSchema } from '@/utils/article-schema';
import { buildPageName, type DataLayerPageType, type RouteAnalytics } from '@/analytics/data-layer';
import { getPublicOrigin } from '@/utils/schema-url';
import { resolvePageMetaDescription } from '@/lib/seo/description';
import { resolveOgImage } from '@/lib/seo/og-image';
import { firstTileImageUrl } from '@/components/product-grid/to-product-tile-card-props';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { widenImageUrl } from '@/lib/images/widen-image';
import {
    getInitialFiltersOpen,
    getSearchWithoutClientOnlyParams,
    getSearchWithoutFiltersParam,
} from '@/hooks/use-filters-panel-state';
import { getLogger } from '@/lib/logger.server';
import { LANDING_PAGE_TYPE } from '@/lib/page-designer/page-types';

@PageType({
    name: 'Product Listing Page',
    description: 'Product listing page with product listings and personalized content',
    supportedAspectTypes: ['plp'],
})
@RegionDefinition([
    {
        id: 'plpTopFullWidth',
        name: 'Top Full Width Region',
        description: 'Full screen width region at the top of the results',
        maxComponents: 5,
    },
    {
        id: 'plpTopContent',
        name: 'Top Content Region',
        description: 'Content width region below sort/filter, above product grid',
        maxComponents: 5,
    },
    {
        id: 'plpBottom',
        name: 'Bottom Region',
        description: 'Region at the bottom of search results after product grid',
        maxComponents: 5,
    },
])
export class ProductListingPageMetadata {
    /* v8 ignore start - decorators are exercised by the generate-cartridge step, not unit tests */
    @AttributeDefinition({
        id: 'settings',
        name: 'Default Settings',
        description:
            'Theme applied to the product-grid section (title, filters, and tiles). Defaults to the dark (black) theme; set to light for a light background.',
        type: 'custom',
        editorDefinition: { type: 'inline.defaultSettings' },
    })
    settings?: DefaultSettingsValue;

    @AttributeDefinition({
        id: 'title',
        name: 'Section Title',
        description:
            'Optional display title shown above the product grid (with a trailing arrow). Falls back to the category name. Does not change the page ID or its URL.',
        type: 'string',
        required: false,
    })
    title?: string;
    /* v8 ignore stop */
}

/**
 * Page-level settings the merchandiser configures on the Product Listing Page type. These arrive in
 * the fetched page's `data` bag (the page analog of a component's `data`) — see {@link resolvePlpTheme}.
 */
type PlpPageSettings = {
    settings?: DefaultSettingsValue;
    title?: string;
};

/**
 * Resolve the PLP section theme from the page settings. Unlike {@link defaultSettingsProps} (which
 * defaults to light), the PLP defaults to **dark** (UCR-2819): the black grid background is the
 * baseline and a merchandiser opts into light.
 */
function resolvePlpTheme(settings?: DefaultSettingsValue): DefaultSettingsTheme {
    return settings?.themeColor === 'light' ? 'light' : 'dark';
}

/**
 * Utility landing-page slugs → their Adobe Analytics page type (UCR-2938). About/FAQ/Contact are
 * authored as root-slug Landing Pages here, but the SDR classifies them distinctly; everything else is
 * generic `content`. Keyed by lowercased slug.
 */
const LANDING_ANALYTICS_TYPES: Record<string, DataLayerPageType> = {
    about: 'about',
    'about-us': 'about',
    faq: 'faq',
    faqs: 'faq',
    contact: 'contact',
    'contact-us': 'contact',
};

/** Analytics payload for a Landing Page (UCR-2938): classify known utility slugs, else `content`. */
function landingAnalytics(slug: string): RouteAnalytics {
    const pageType = LANDING_ANALYTICS_TYPES[slug.toLowerCase()] ?? 'content';
    const pageName = pageType === 'content' ? buildPageName('content', slug) : buildPageName(pageType);
    return { page: { pageName, pageType } };
}

/* v8 ignore start - decorators are exercised by the generate-cartridge step, not unit tests */
/**
 * Landing Page page type (UCR-2789). A standalone, merchandiser-authored marketing page served at a
 * **root slug** (`/<slug>`, where the slug is the Page Designer page id) — not under any prefix. It
 * is co-located in this route file on purpose: a root single-segment URL is matched by this dynamic
 * route (`/:categoryId`) — React Router scores a param segment above the `_app.$` splat — so the
 * loader below dispatches category-first, then falls back to a Landing Page. Co-location also gives
 * the generated `landingPage.json` this file's `/:categoryId` route, which Page Designer uses to
 * build the editor preview URL.
 *
 * The Thumbnail and Description settings mirror the Article page type so a Landing Page can be
 * referenced from tiles/links and feed SEO. Landing Pages are intentionally excluded from the
 * `/learn` article listing — see {@link LANDING_PAGE_TYPE}.
 */
@PageType({
    name: 'Landing Page',
    description:
        'A standalone landing page served at a root URL (the page id is the slug). Author the body in the Content region; the Thumbnail and Description settings feed links/tiles and SEO. Excluded from the Learn article listing.',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'main',
        name: 'Content',
        description: 'The landing page body — accepts any Page Designer component.',
    },
])
export class LandingPageMetadata {
    @AttributeDefinition({
        id: 'thumbnail',
        name: 'Thumbnail',
        description: 'Widen DAM image used as the landing page thumbnail when linked from tiles/listings.',
        type: 'custom',
        editorDefinition: {
            type: 'inline.widenImage',
            configuration: {
                showSizes: false,
                showImageAlignment: false,
                showQualitySlider: false,
                showGridCoordinate: false,
            },
        },
    })
    thumbnail?: WidenImageValue;

    @AttributeDefinition({
        id: 'articleDescription',
        name: 'Description',
        description:
            'Short summary used when the landing page is linked from tiles and listings. For the search/social meta description, fill in SEO Description under the page settings instead.',
        type: 'text',
    })
    articleDescription?: string;
}
/* v8 ignore stop */

type CategoryPageData = {
    category: ShopperProducts.schemas['Category'];
    searchResultCritical: ShopperSearch.schemas['ProductSearchResult'];
    searchResultNonCritical: Promise<ShopperSearch.schemas['ProductSearchResult']>;
    // The loader awaits the page (so theme/title are ready at first paint), but `<Region>` also
    // accepts the unresolved promise — allow both so test fixtures can pass a resolved promise.
    page: Awaited<ReturnType<typeof fetchPageWithComponentData>> | ReturnType<typeof fetchPageWithComponentData>;
    /** Section theme from the PLP page settings (defaults to dark). @see resolvePlpTheme */
    plpTheme?: DefaultSettingsTheme;
    /** Footer background theme = the theme of the last component on the page (defaults to light). */
    footerTheme?: DefaultSettingsTheme;
    /** Merchandiser-editable display title; falls back to the category name. Distinct from the page id/URL. */
    plpTitle?: string;
    categoryId: string;
    pageUrl: string;
    /** Authored meta description: category page description → aspect page SEO Description → none. */
    metaDescription?: string;
    /** Social share image: first authored region image → first product tile → Black Magic mark. */
    ogImageUrl: string;
    refine: string[];
    currency: string;
    locale: string;
    initialFiltersOpen?: boolean;
    categorySchema: Promise<ReturnType<typeof generateCategorySchema> | null>;
    /** Standalone BreadcrumbList JSON-LD (UCR-3005). Resolved — the category is awaited anyway. */
    breadcrumbSchema?: BreadcrumbListSchema | null;
    wishlistInitialState: Promise<WishlistInitialState>;
    /** Adobe Analytics data-layer payload for the PLP (UCR-2938). */
    analytics?: RouteAnalytics;
};

/** A Landing Page resolved at a root slug (UCR-2789). */
type LandingPageData = {
    kind: 'landing';
    page: PageWithComponentData;
    /** Footer background theme = the theme of the last component in `main` (defaults to light). */
    footerTheme?: DefaultSettingsTheme;
    pageUrl: string;
    title: string;
    description?: string;
    /** Social share image: Thumbnail setting → first image in the body → Black Magic mark. (UCR-3023) */
    ogImageUrl: string;
    /** Article JSON-LD (schema.org @graph). Null when there's no title to describe. (UCR-2783) */
    articleSchema: ArticleSchema | null;
    /** Adobe Analytics data-layer payload (UCR-2938). Landing pages are classified as `content`. */
    analytics?: RouteAnalytics;
};

/**
 * Discriminated union the loader returns: a category PLP, or a root-slug Landing Page. Only the
 * Landing variant carries a `kind` field (the category variant is left untouched), so consumers
 * discriminate with `'kind' in data`.
 */
type RootSlugData = CategoryPageData | LandingPageData;

/**
 * Resolve a root slug to a Landing Page, or `null` if it isn't one. The slug is the Page Designer
 * page id. We **type-guard on `page.typeId`** so only `landingPage`-typed pages may render at a root
 * URL — a slug that happens to match some other page id (e.g. `footer`, an article) is rejected and
 * falls through to a 404. In Page Designer edit/preview the page id arrives via the query string,
 * which {@link fetchPageWithComponentData} already honors, so the editor preview resolves here too.
 */
async function resolveLandingPage(args: Route.LoaderArgs, slug: string): Promise<LandingPageData | null> {
    const page = await fetchPageWithComponentData(args, { pageId: slug });
    if (!page || page.typeId !== LANDING_PAGE_TYPE) {
        return null;
    }
    const requestUrl = new URL(args.request.url);
    const publicOrigin = getPublicOrigin(args.request);
    const title = page.pageTitle ?? page.name ?? 'Landing Page';
    // Meta description is authored on the page — SEO Description, then the internal page
    // description. Never a hardcoded string; unset means no tag. (UCR-3023)
    const description = resolvePageMetaDescription(page);

    // Article JSON-LD (UCR-2783). Landing pages mirror the Article page type (thumbnail + description
    // page settings, delivered in `page.data`) and emit the same Article @graph. The @id/author/image
    // use the public origin (not the internal request URL).
    const settings = page.data as { thumbnail?: WidenImageValue; articleDescription?: string } | undefined;
    const thumb = settings?.thumbnail;
    const thumbUrl = thumb?.webpURL || thumb?.fileURL;
    const articleSchema = generateArticleSchema({
        origin: publicOrigin,
        path: requestUrl.pathname,
        headline: title,
        // Raw authored text, deliberately not the truncated `description` above — structured data has
        // no snippet-length limit, so the meta tag's word-boundary cut and trailing ellipsis must not
        // leak into the schema. Kept identical to the Article route, which feeds this same builder.
        // (UCR-3023)
        description: settings?.articleDescription ?? page.pageDescription ?? page.description,
        imageUrls: thumbUrl ? [widenImageUrl(thumbUrl)] : [],
        config: getConfig(args.context),
    });

    return {
        kind: 'landing',
        page,
        footerTheme: resolveLastComponentTheme(page, ['main']),
        pageUrl: buildCanonicalUrl(publicOrigin, requestUrl.pathname, requestUrl.search),
        title,
        description,
        // This page type declares a Thumbnail, so it wins; otherwise the first image in the body.
        ogImageUrl: resolveOgImage({
            origin: publicOrigin,
            thumbnail: thumb,
            page,
            regionIds: ['main'],
        }),
        articleSchema,
        analytics: landingAnalytics(slug),
    };
}

/**
 * Server-side loader. A root single-segment URL lands here (the dynamic `:categoryId` route outscores
 * the `_app.$` splat). It dispatches **category-first**: if the slug is a category, render the PLP;
 * otherwise fall back to a Landing Page; otherwise 404. Category precedence means a Landing Page can
 * never shadow an existing category URL.
 * @returns Category PLP data, or Landing Page data.
 */
export async function loader(args: Route.LoaderArgs): Promise<RootSlugData> {
    const {
        context,
        request,
        params: { categoryId },
    } = args;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const logger = getLogger(context);
    logger.debug('Category: loader starting', {
        categoryId,
        offset: parseInt(searchParams.get('offset') || '0', 10),
    });
    const offset = parseInt(getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.OFFSET) || '0', 10);
    const sort = getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.SORT);
    const refine = getAllQueryParams(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.REFINE);
    const initialFiltersOpen = getInitialFiltersOpen(searchParams);

    // Get currency and locale for cache-busting the page key
    const config = getConfig(context);
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Category: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;
    const locale = siteCtx.locale.id;
    const limit = config.search.products.hits.limit;

    let categoryData: ShopperProducts.schemas['Category'] | undefined;
    try {
        categoryData = await fetchCategory(context, categoryId, 1);
    } catch (e) {
        // Not a category? A 404 means the slug might instead be a Landing Page (UCR-2789). Try that
        // before surfacing the 404; any non-404 (auth, 5xx) is a real error and is re-thrown as-is.
        if (e instanceof NormalizedApiError && e.status === 404) {
            const landing = await resolveLandingPage(args, categoryId);
            if (landing) {
                return landing;
            }
            throw new Response(e.message, { status: 404 });
        }
        if (e instanceof NormalizedApiError && e.status) {
            throw new Response(e.message, { status: e.status });
        }
        throw new Response('Internal Server Error', { status: 500 });
    }

    // A category may delegate its URL to a Landing Page via the `c_landingPageId` custom attribute —
    // e.g. a nav-only "About" category whose URL should render the published About landing page
    // instead of a product grid. The catalog-driven nav still links to the category (so the menu item
    // works), but when the category carries this attribute and the referenced landing page resolves,
    // we render the landing page here. This is the merchandiser-controlled exception to the
    // category-first precedence (a plain category URL still never resolves to a landing page). (UCR-2789)
    const landingPageId = (
        categoryData as (ShopperProducts.schemas['Category'] & { c_landingPageId?: string }) | undefined
    )?.c_landingPageId?.trim();
    if (landingPageId) {
        const landing = await resolveLandingPage(args, landingPageId);
        if (landing) {
            return landing;
        }
        logger.warn('Category c_landingPageId is set but the landing page did not resolve', {
            categoryId,
            landingPageId,
        });
    }

    // Keep non-category refinements and apply exactly one category refinement.
    // If URL already contains a cgid refine (e.g. from quick filters), honor it.
    // Otherwise, default to the category id from the route path.
    const effectiveRefine = refine.filter((r) => !r.startsWith('cgid='));
    const selectedCgidRefine = refine.find((r) => r.startsWith('cgid='));
    effectiveRefine.push(selectedCgidRefine ?? `cgid=${categoryId}`);

    // Kick off the Page Designer aspect page in parallel with the (critical) product search. Its
    // page-level settings drive the section theme and title, which are above the fold (SEO H1 +
    // whole-section background), so we await it before returning to avoid a theme/title flash.
    const pagePromise = fetchPageWithComponentData(args, {
        aspectType: 'plp',
        categoryId,
    });

    // Ensure criticalCount doesn't exceed limit to prevent negative non-critical limit
    const criticalCount = config.search.products.hits.critical ?? 4;
    const safeCriticalCount = Math.min(criticalCount, limit);
    const searchResultCritical = await fetchSearchProducts(context, {
        limit: safeCriticalCount,
        offset,
        sort,
        refine: effectiveRefine,
        currency,
    });

    const effectiveCriticalCount = searchResultCritical.hits?.length ?? 0;
    const searchResultNonCritical = fetchSearchProducts(context, {
        limit: limit - effectiveCriticalCount,
        offset: offset + effectiveCriticalCount,
        sort,
        refine: effectiveRefine,
        currency,
    });

    // Schema URLs use the public origin from the request headers instead of request.url, so internal
    // AWS Lambda / MRT origins never leak into JSON-LD — and, since UCR-3023, into og:url either.
    const publicOrigin = getPublicOrigin(request);

    const pageUrl = buildCanonicalUrl(publicOrigin, requestUrl.pathname, requestUrl.search);
    const schemaPageUrl = `${publicOrigin}${requestUrl.pathname}${requestUrl.search}`;

    // Breadcrumb JSON-LD (UCR-3005), emitted as its own node so crawlers and validators that walk
    // top-level nodes see it — the PLP previously nested it inside CollectionPage, where it was
    // invisible to them. Built from the already-awaited category, so it needs no Suspense boundary
    // and lands in the initial HTML. Same builder as the PDP, so both pages agree on the hierarchy.
    const breadcrumbSchema = generateBreadcrumbSchema(
        buildCategoryCrumbs({
            category: categoryData,
            origin: publicOrigin,
            currentPageUrl: schemaPageUrl,
            homeName: getTranslation(context).t('category:breadcrumbs.home'),
        })
    );

    // Generate category schema in loader (server-side) for SEO
    const categorySchemaPromise = searchResultNonCritical
        .then((searchResult: ShopperSearch.schemas['ProductSearchResult']) => {
            try {
                // Validate inputs before generating schema
                if (!categoryData || !searchResult) {
                    return null;
                }
                return generateCategorySchema({
                    category: categoryData,
                    searchResult: {
                        ...searchResult,
                        hits: [...(searchResultCritical.hits || []), ...(searchResult.hits || [])],
                    },
                    config,
                    pageUrl: schemaPageUrl,
                    defaultCurrency: currency,
                });
            } catch (error) {
                logger.error('Error generating category schema in loader', {
                    error,
                });
                return null;
            }
        })
        .catch((error) => {
            logger.error('Error in category schema promise chain', {
                error,
            });
            return null;
        });

    const page = await pagePromise;
    const plpSettings = page?.data as PlpPageSettings | undefined;
    const plpTheme = resolvePlpTheme(plpSettings?.settings);
    const plpTitle = plpSettings?.title?.trim() || undefined;
    const footerTheme = resolveLastComponentTheme(page, ['plpTopFullWidth', 'plpTopContent', 'plpBottom']);

    // Analytics data layer (UCR-2938): PLP page + listing context. `filtersApplied` reflects the
    // applied refinements (cgid excluded), in the site:category:value shape; omitted when none.
    const appliedRefineValues = effectiveRefine
        .filter((r) => !r.startsWith('cgid='))
        .map((r) => r.slice(r.indexOf('=') + 1))
        .filter(Boolean);
    const analytics: RouteAnalytics = {
        page: { pageName: buildPageName('shop', categoryId), pageType: 'category' },
        listing: {
            category: categoryId,
            ...(appliedRefineValues.length > 0 && {
                filtersApplied: buildPageName(categoryId, ...appliedRefineValues),
            }),
        },
    };

    return {
        category: categoryData,
        searchResultCritical,
        searchResultNonCritical,
        page,
        plpTheme,
        footerTheme,
        plpTitle,
        categoryId,
        pageUrl,
        // The category's own page description outranks the Page Designer page here: the PLP page is a
        // shared `plp` aspect template covering every category, while `pageDescription` is authored
        // per category. Falls through to the aspect page's SEO Description, then nothing. (UCR-3023)
        metaDescription: resolvePageMetaDescription(page, categoryData.pageDescription || categoryData.description),
        // No thumbnail attribute on this page type: the share image is the first authored image in the
        // Page Designer regions (a category banner, typically), then the first product tile, then the
        // Black Magic mark.
        ogImageUrl: resolveOgImage({
            origin: publicOrigin,
            page,
            regionIds: ['plpTopFullWidth', 'plpTopContent', 'plpBottom'],
            contentImageUrl: firstTileImageUrl(searchResultCritical.hits),
        }),
        refine: effectiveRefine,
        currency,
        locale,
        initialFiltersOpen,
        categorySchema: categorySchemaPromise,
        breadcrumbSchema,
        wishlistInitialState: fetchWishlistInitialState(args.context),
        analytics,
    };
}

export function shouldRevalidate({ currentUrl, nextUrl, defaultShouldRevalidate }: ShouldRevalidateFunctionArgs) {
    const clientOnlyParamsChanged =
        currentUrl.pathname === nextUrl.pathname &&
        currentUrl.search !== nextUrl.search &&
        getSearchWithoutClientOnlyParams(currentUrl.search) === getSearchWithoutClientOnlyParams(nextUrl.search);

    if (clientOnlyParamsChanged) {
        return false;
    }

    return defaultShouldRevalidate;
}

/**
 * Category page component that displays a product category with filtering, sorting, and pagination.
 * This component uses the createPage factory to handle Suspense patterns.
 * @returns JSX element representing the category page
 */
function ProductGridError() {
    const rawError = useAsyncError();
    const error = rawError instanceof NormalizedApiError ? rawError : null;
    const { t } = useTranslation('common');
    return (
        <div role="alert" className="col-span-full py-8 text-center text-muted-foreground">
            <p>{t('productGrid.loadFailed')}</p>
            {import.meta.env.DEV && error && (
                <div className="mt-2 text-xs font-mono text-muted-foreground/70">
                    {error.status && <span>{error.status} </span>}
                    {error.message && <p>{error.message}</p>}
                </div>
            )}
        </div>
    );
}

/**
 * Component that renders JSON-LD schema when categorySchema promise resolves.
 * Must be inside Suspense boundary to ensure it streams correctly in SSR.
 */
function CategoryJsonLd({
    categorySchemaPromise,
}: {
    categorySchemaPromise: Promise<ReturnType<typeof generateCategorySchema> | null>;
}) {
    const categorySchema = use(categorySchemaPromise);
    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const nonce = rootData?.nonce ?? undefined;
    return categorySchema ? <JsonLd data={categorySchema} id="category-schema" nonce={nonce} /> : null;
}

/**
 * Renders a root-slug Landing Page (UCR-2789): SEO + the authored `main` region. The region is
 * rendered **full-width** (not capped at `max-w-content-full`) — matching the homepage/PLP/article
 * `main` — so full-bleed Page Designer components (Content Container, Full Width Image, …) bleed
 * edge-to-edge and constrain their own interior via their inner `.section-container`. `-mt-8`
 * cancels the global `main` top padding so the first component sits flush to the header.
 */
function LandingView({ data }: { data: LandingPageData }) {
    const nonce = useRouteLoaderData<typeof rootLoader>('root')?.nonce ?? undefined;
    return (
        <div className="-mt-8">
            <SeoMeta
                title={data.title}
                description={data.description}
                openGraph={{ type: 'website', url: data.pageUrl, image: data.ogImageUrl }}
            />
            {data.articleSchema && <JsonLd data={data.articleSchema} id="article-schema" nonce={nonce} />}
            <Region page={data.page} regionId="main" />
        </div>
    );
}

/**
 * Root-slug dispatcher. Renders the Landing Page or the category PLP as **distinct component types**
 * (rather than branching inside one component) so navigating between a landing page and a category
 * swaps the mounted component instead of changing a single component's hook count.
 */
export default function RootSlugPage({ loaderData }: { loaderData: RootSlugData }) {
    return 'kind' in loaderData ? <LandingView data={loaderData} /> : <CategoryView loaderData={loaderData} />;
}

function CategoryView({
    loaderData: {
        category,
        searchResultCritical,
        searchResultNonCritical,
        page,
        plpTheme,
        plpTitle,
        categoryId,
        pageUrl,
        metaDescription,
        ogImageUrl,
        locale,
        currency,
        categorySchema,
        breadcrumbSchema,
        wishlistInitialState,
    },
}: {
    loaderData: CategoryPageData;
}) {
    const config = useConfig();
    const nonce = useRouteLoaderData<typeof rootLoader>('root')?.nonce ?? undefined;

    const limit = config.search.products.hits.limit;

    // Determine the maximum number of skeletons to display in the product grid.
    // Out-of-the-box the idea is to not display more than 8 skeletons, i.e., two rows on a desktop device.
    // Wrap in Math.max(0, ...) to prevent negative values when criticalCount is high(er).
    const criticalCount = searchResultCritical.hits?.length ?? 0;
    const nonCriticalCount = Math.max(
        0,
        Math.min(8, limit, searchResultCritical.total - searchResultCritical.offset) - criticalCount
    );

    const analytics = useAnalytics();
    const lastTrackedDataRef = useRef<string | null>(null);

    const location = useLocation();
    const navigation = useNavigation();
    const searchWithoutFiltersParam = useMemo(() => getSearchWithoutFiltersParam(location.search), [location.search]);
    const pageIdentity = `${categoryId}-${currency}-${locale}`;
    const analyticsKey = `${pageIdentity}-${searchWithoutFiltersParam}-${location.hash}`;
    const productGridDataKey = `${pageIdentity}-${searchWithoutFiltersParam}`;
    const isProductGridLoading = useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) {
            return false;
        }
        const currentRefines = new URLSearchParams(location.search).getAll('refine');
        const nextRefines = new URLSearchParams(navigation.location.search).getAll('refine');
        return (
            currentRefines.length !== nextRefines.length ||
            currentRefines.some((currentRefine, index) => currentRefine !== nextRefines[index])
        );
    }, [location.search, navigation.location, navigation.state]);

    const nonCriticalPromise = useMemo(
        () => searchResultNonCritical.then((r) => r.hits ?? []),
        [searchResultNonCritical]
    );

    const [, startTransition] = useTransition();

    useEffect(() => {
        // Only track if we haven't already tracked this specific data combination
        if (analyticsKey !== lastTrackedDataRef.current) {
            lastTrackedDataRef.current = analyticsKey;

            startTransition(() => {
                void nonCriticalPromise
                    .then((searchHitsData: ShopperSearch.schemas['ProductSearchHit'][]) => {
                        if (analytics) {
                            void analytics.trackViewCategory({
                                category,
                                searchResults: [...(searchResultCritical.hits ?? []), ...searchHitsData],
                                sort:
                                    searchResultCritical.selectedSortingOption ||
                                    searchResultCritical.sortingOptions?.[0]?.label ||
                                    '',
                                refinements: searchResultCritical.selectedRefinements ?? {},
                            });
                        }
                    })
                    .catch(() => {
                        // Silently handle promise rejection
                    });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, category, analyticsKey, nonCriticalPromise]);

    const handleProductClick = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit']) => {
            if (analytics) {
                void analytics.trackClickProductInCategory({
                    category,
                    product,
                });
            }
        },
        [analytics, category]
    );

    return (
        <WishlistProvider initialState={wishlistInitialState}>
            <SeoMeta
                title={category.name || category.id}
                description={metaDescription}
                openGraph={{
                    type: 'website',
                    url: pageUrl,
                    image: ogImageUrl,
                }}
            />
            <div className="-mt-8">
                {/* plpTopFullWidth — full-width banner region, flush to the header (mirrors homepage pattern) */}
                <Region
                    page={page}
                    regionId="plpTopFullWidth"
                    fallbackElement={<CategoryBannerSkeleton />}
                    errorElement={<CategoryBanner />}
                    fallbackOnEmpty
                />

                {/*
                 * The grid section (breadcrumbs + title + filters + tiles) carries the merchandiser
                 * theme. The theme wrapper is full-bleed so the background color spans the viewport;
                 * a `section-container` child constrains the content to the page width. Defaults to
                 * dark (UCR-2819). The dark PLP surface is **Black** (`--bm-black` #0F0C00) with
                 * organic-white text — a shade deeper than the section theme's `--bm-ink`, so the
                 * Organic-Black (#1C1603) tile panels sit distinctly on top; it's set with explicit
                 * utilities rather than `bm-theme-dark`, whose unlayered `--bm-ink` background would beat
                 * a utility override. The light option uses the shared `bm-theme-light`. The `plpBottom`
                 * Page Designer region stays outside (below) this wrapper and full-width.
                 */}
                <div
                    className={cn(plpTheme === 'light' ? 'bm-theme-light' : 'bg-bm-black text-bm-organic-white')}
                    data-slot="plp-grid-section">
                    <div className="section-container pb-16 pt-8">
                        <BreadcrumbSection className="mb-4">
                            <CategoryBreadcrumbs category={category} />
                        </BreadcrumbSection>

                        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                            <div className="flex items-end gap-3">
                                <h1 className="type-h3">{plpTitle || category?.name || category.id}</h1>
                                <ArrowDownRightIcon
                                    aria-hidden
                                    className="size-[18px] shrink-0 md:size-[27px]"
                                    data-slot="plp-title-arrow"
                                />
                            </div>
                            <UITarget targetId="sfcc.plp.search.summary" />
                            {searchResultCritical?.sortingOptions && searchResultCritical.sortingOptions.length > 0 && (
                                <div className="flex-shrink-0">
                                    <CategorySorting result={searchResultCritical} />
                                </div>
                            )}
                        </div>

                        {/* Filters (Filters button, refinements panel, quick-filter chips, active
                            filters) are intentionally hidden on the PLP page type (UCR-2940). */}
                        <div>
                            {/* plpTopContent */}
                            <Region className="mb-8" page={page} regionId="plpTopContent" />

                            <UITarget targetId="sfcc.plp.agent.categoryHelper" />
                            <UITarget targetId="sfcc.plp.search.results">
                                <DeferredProductGrid
                                    key={productGridDataKey}
                                    critical={searchResultCritical.hits ?? []}
                                    nonCritical={nonCriticalPromise}
                                    nonCriticalCount={nonCriticalCount}
                                    isLoading={isProductGridLoading}
                                    handleProductClick={handleProductClick}
                                    topCategoryName={
                                        category.parentCategoryTree?.find((p) => p.id !== 'root')?.name ?? category.name
                                    }
                                    errorElement={<ProductGridError />}
                                />
                            </UITarget>

                            {searchResultCritical.total > 1 && (
                                <div className="mt-10">
                                    <CategoryPagination
                                        limit={limit}
                                        offset={searchResultCritical.offset}
                                        total={searchResultCritical.total}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* plpBottom — full-width PD region below the grid section (outside the theme wrapper)
                    so its components manage their own background color and content width. */}
                <Region className="mt-8" page={page} regionId="plpBottom" />
            </div>
            {/* Breadcrumb JSON-LD (UCR-3005) — a standalone node, rendered unsuspended so it ships
                in the initial HTML. The CollectionPage below waits on the deferred product search. */}
            {breadcrumbSchema && <JsonLd data={breadcrumbSchema} id="breadcrumb-schema" nonce={nonce} />}
            <Suspense fallback={null}>
                <CategoryJsonLd categorySchemaPromise={categorySchema} />
            </Suspense>
        </WishlistProvider>
    );
}
